import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {BootstrapTable, TableHeaderColumn} from 'react-bootstrap-table';
import {useIntl} from 'react-intl';
import {useHotkeys} from 'react-hotkeys-hook';
import {ModalBody, ModalFooter} from 'reactstrap';
import Button from '../button';
import ReactExport from 'react-export-excel';
import {ThemeContext} from '../../apiContexts';
import {ModalStyled} from '../../styles';
import I18nMessage from '../I18nMessage';
import {flattenObj} from './flattenObj';
import SearchField from './searchField';
import {SEARCH_MIN_LENGTH} from '../../helpers/search';
import {computeColumnWidths, toProportionalPercentages} from '../../helpers/columnWidth';
import {ThemedTableWrapper} from './style';


const ExcelFile = ReactExport.ExcelFile;
const ExcelSheet = ReactExport.ExcelFile.ExcelSheet;
const ExcelColumn = ReactExport.ExcelFile.ExcelColumn;

const DataTable = (props) => {
    const {
        list,
        headers,
        add,
        edit,
        permissions,
        accumulatorForm,
        myCompanyForm,
        remove,
        confirmText,
        keyField,
        noDataText,
        searchPlaceholder,
        search,
        loading = false,
        excelConfig,
        // Telas cujas colunas de planilha nao cabem nas linhas da tela passam uma funcao aqui; ela
        // so e chamada no clique do download.
        excelData,
        auth,
        showGroupButtons = true,
        extraHeaderEl,
        finesForm,
        onSelectRow,
    } = props;


    const intl = useIntl();
    const theme = useContext(ThemeContext);
    const [row, setSelectedRow] = useState(null);
    const [modal, toggle] = useState(false);
    // The spreadsheet payload is only read when the download runs, so it is built when the export is
    // asked for and thrown away right after, instead of on every redraw of the table.
    const [exporting, setExporting] = useState(false);
    const isAdmin = auth ? auth.profile ? (auth.profile === 'admin' || auth.profile === 'monitor') : true : true;

    useEffect(() => {
        if (exporting) setExporting(false);
    }, [exporting]);

    const editAction = useCallback(() => {
        if (row) {
            edit(row);
        }
    }, [row]);

    const permissionAction = useCallback(() => {
        if (row) {
            permissions(row);
        }
    }, [row]);

    const accumulatorAction = useCallback(() => {
        if (row && accumulatorForm) {
            accumulatorForm(row);
        }
    }, [row, accumulatorForm]);

    const myCompanyAction = useCallback(() => {
        myCompanyForm();
    }, []);

    const removeAction = useCallback(() => {
        if (row) {
            toggle(!modal);
        }
    }, [row]);

    const f3KeyBind = useCallback(() => {
        if (permissions) {
            permissionAction();
        } else if (myCompanyForm) {
            myCompanyAction();
        }
    }, [permissionAction, myCompanyAction]);

    useHotkeys('F1', () => add({}));
    useHotkeys('F2', () => editAction(), {}, [editAction]);
    useHotkeys('F3', f3KeyBind, {}, [f3KeyBind]);
    useHotkeys('F4', () => accumulatorAction(), {}, [row]);
    useHotkeys('ctrl+r', () => removeAction(), {}, [row]);

    const onRowSelect = (row, isSelected, e) => {
        if (isSelected && row.id === 0) {
            return false;
        }
        setSelectedRow(isSelected ? row : null);
        if (onSelectRow) {
            onSelectRow(isSelected ? row : null, e);
        }
        return true;
    };

    const bgColor = (row, isSelect) => {
        if (isSelect && row.id === 0) {
            return null;
        }
        return '#2497e9';
    };

    const selectRowProp = {
        mode: 'radio',
        bgColor: bgColor,
        hideSelectColumn: true,
        clickToSelect: true,
        onSelect: onRowSelect,
    };

    const deleteRow = (row) => {
        remove(row);
        toggle(!modal);
    };

    const formattConfirmText = (text) => {
        if (text && row) {
            const field = text.match(/<field>(.*?)<\/field>/g).map(function(val) {
                return val.replace(/<\/?field>/g, '');
            });

            return text.replace(field, row[field[0]]);
        }
        return '';
    };

    const renderAlertConfirmation = () => {
        const msgRemove =
            formattConfirmText(confirmText) ||
            intl.formatMessage({id: 'sharedRemoveConfirm'});
        return (
            <ModalStyled
                theme={theme}
                isOpen={modal}
                toggle={toggle}
                className="customModal"
            >
                <ModalBody className="d-flex flex-column justify-content-center align-items-center">
                    <span className="fal fa-exclamation-triangle fa-5x text-danger mb-3"/>
                    <span dangerouslySetInnerHTML={{__html: msgRemove}}/>
                </ModalBody>
                <ModalFooter>
                    <Button
                        outline
                        color="secondary"
                        onClick={() => toggle()}
                        className=" custom-border"
                    >
                        <I18nMessage id="sharedCancel"/>
                    </Button>
                    <Button
                        color="danger"
                        onClick={() => deleteRow(row)}
                        className="custom-shadow custom-border"
                    >
                        <I18nMessage id="sharedRemove"/>
                    </Button>
                </ModalFooter>
            </ModalStyled>
        );
    };

    const renderExcelColumn = excelConfig && excelConfig.columns.map((item, idx) => {
        return (
            <ExcelColumn key={idx} label={`${intl.formatMessage({id: item.label})}`} value={item.field}/>
        );
    });


    const createCustomToolBar = (props) => {
        return (
            <>
                <div className={'rounded-group-button'}>
                    {extraHeaderEl && extraHeaderEl(row, (!row || loading))}
                    {showGroupButtons && <>
                    { isAdmin && <Button
                        className={'outline'}
                        onClick={() => add({})}
                        size={'sm'}
                        color={'theme'}>
                        <i className="fas fa-plus"/> <I18nMessage id="sharedAdd" /> [F1]
                    </Button> }
                    { isAdmin && <Button
                        disabled={!row || loading}
                        className={'outline'}
                        size={'sm'}
                        color={'danger'}
                        onClick={() => removeAction()}
                    >
                        <i className="fas fa-minus"/> <I18nMessage id="sharedRemove" /> [ctrl+r]
                    </Button> }
                    { isAdmin && <Button
                        disabled={!row || loading}
                        className={'outline'}
                        size={'sm'}
                        color={'theme'}
                        onClick={() => edit(row)}
                    >
                        <i className="fas fa-edit"/> <I18nMessage id="sharedEdit" /> [F2]
                    </Button> }
                    {permissions && (
                        <Button
                            disabled={!row || loading}
                            className={'outline'}
                            color={'theme'}
                            size={'sm'}
                            onClick={() => permissions(row)}
                        >
                            <i className="fas fa-link"/> <I18nMessage id="sharedConnect" /> [F3]
                        </Button>
                    )}
                    {myCompanyForm && (
                        <Button
                            disabled={loading}
                            className={'outline'}
                            color={'theme'}
                            size={'sm'}
                            onClick={() => myCompanyForm(row)}
                        >
                            <i className="fad fa-building"/> <I18nMessage id="sharedMyCompany" /> [F3]
                        </Button>
                    )}
                    {accumulatorForm && (
                        <Button
                            disabled={!row || loading}
                            className={'outline'}
                            color={'theme'}
                            size={'sm'}
                            onClick={() => accumulatorForm(row)}
                        >
                            <i className="fas fa-link"/> <I18nMessage id="deviceOdometerHourmeter" /> [F4]
                        </Button>
                    )}
                    {finesForm && (
                        <Button
                            disabled={!row || loading}
                            className={'outline'}
                            color={'theme'}
                            size={'sm'}
                            onClick={() => finesForm(row)}
                        >
                            <i className="fas fa-file-invoice-dollar"/> <I18nMessage id="certadocCheckFines" />
                        </Button>
                    )}
                    {excelConfig && (
                        <>
                            <Button className={'outline'}
                                    size={'sm'}
                                    color={'warning'}
                                    onClick={() => setExporting(true)}>
                                <i className="fas fa-download"/>
                            </Button>
                            {exporting && (
                                <ExcelFile hideElement>
                                    <ExcelSheet data={flattenObj(excelData ? excelData() : list)}
                                                name={`${intl.formatMessage({id: excelConfig.fileName})}`}>
                                        {renderExcelColumn}
                                    </ExcelSheet>
                                </ExcelFile>
                            )}
                        </>
                    )}
                    </>}
                </div>
                <div className="search-toolbar">{props.components.searchPanel}</div>
            </>
        );
    };

    const options = {
        toolBar: createCustomToolBar,
        searchField: (fieldProps) => (
            <SearchField
                {...fieldProps}
                minLength={SEARCH_MIN_LENGTH}
                hint={(min) => intl.formatMessage({id: 'searchMinLength'}, {min})}
            />
        ),
        noDataText: noDataText || intl.formatMessage({id: 'sharedNoDataText'}),
        sizePerPageList: [{
            text: '10', value: 10,
        }, {
            text: '50', value: 50,
        }, {
            text: intl.formatMessage({id: 'sharedAll'}), value: list.length,
        }],
        sizePerPage: 10, // which size per page you want to locate as default
        prePage: intl.formatMessage({id: 'sharedPrev'}), // Previous page button text
        nextPage: intl.formatMessage({id: 'sharedNext'}), // Next page button text
        firstPage: intl.formatMessage({id: 'sharedFirst'}), // First page button text
        lastPage: intl.formatMessage({id: 'sharedLast'}), // Last page button text
    };

    const columnFormatter = (cell, row) => {
        if (typeof cell === 'boolean') {
            if (cell) {
                return intl.formatMessage({id: 'sharedYes'});
            }
            return intl.formatMessage({id: 'sharedNo'});
        }
        return cell;
    };

    // Largura por conteúdo real: nunca menor que o próprio título, nunca menor que as células mais
    // longas da lista inteira (não corta texto; amostra dos textos mais longos, ver sampleWidest).
    // Calculado sobre `list` inteira, não só a página
    // visível: espelhar página/busca exigiria `options.onPageChange`/`afterSearch`, mas `options`
    // é recriado a cada render e a lib trata qualquer mudança de identidade como estado novo, o
    // que entraria em loop de re-render. `item.maxWidthRatio` limita a coluna a título×1.5 mesmo
    // com conteúdo mais longo. Coluna ordenável ganha padding extra pro ícone de seta não
    // espremer o texto do título.
    //
    // A coluna com `width` (px) usa esse valor e não mede nada: em lista grande, poucos textos muito
    // longos deixam a coluna medida várias vezes mais larga que o comum. As telas tiram esse valor da
    // base de produção (texto do percentil 95 da coluna mais a folga da célula). O texto que não cabe aparece
    // cortado, e inteiro no título da célula.
    const SORT_CARET_PADDING_PX = 20;
    const columnWidths = useMemo(() => {
        const measured = headers.filter((item) => !item.width);
        const widths = measured.length ? computeColumnWidths(measured.map((item) => ({
            key: item.field,
            title: intl.formatMessage({id: item.title}),
            rows: list,
            pick: (row) => columnFormatter(row[item.field], row),
            maxWidthRatio: item.maxWidthRatio,
            extraPaddingPx: item.dataSort ? SORT_CARET_PADDING_PX : 0,
        }))) : {};
        headers.forEach((item) => {
            if (item.width) widths[item.field] = item.width;
        });
        return widths;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headers, list, intl]);

    // O card da tela (Content, em admin-form-container.jsx) tem largura fixa, não encolhe pelo
    // conteúdo da tabela -- então a tabela sempre ocupa 100% dele (table-layout:fixed +
    // width:100% do Bootstrap global). Os 100% se dividem em % proporcional ao px calculado
    // acima -- uma coluna com o dobro de conteúdo de outra fica com o dobro do espaço, sem sobrar
    // faixa em branco depois da última coluna nem estourar o card. Quando a soma real ultrapassa
    // o card, tableMinWidth (px, não %) mantém o piso via --table-min-width e aciona o scroll
    // (index.sass).
    const columnPercentages = toProportionalPercentages(columnWidths);

    const renderTableHeaderColumn = headers.map((item, idx) => {
        const format = item.dataFormat || columnFormatter;
        return (
            <TableHeaderColumn
                key={idx}
                dataField={item.field}
                dataSort={item.dataSort}
                dataFormat={format}
                columnTitle={(cell, row) => {
                    const text = format(cell, row);
                    return typeof text === 'string' || typeof text === 'number' ? String(text) : null;
                }}
                filter={item.filter}
                width={columnPercentages[item.field]}>
                <I18nMessage id={item.title}/>
            </TableHeaderColumn>
        );
    });

    const tableMinWidth = headers.reduce((sum, item) => sum + (columnWidths[item.field] || 0), 0);

    const renderTable = () => {
        const data = list.length > 0 ? list : [];

        return (
            <ThemedTableWrapper theme={theme}>
                <BootstrapTable
                    options={options}
                    data={data}
                    search={search || true}
                    keyField={keyField}
                    selectRow={selectRowProp}
                    tableStyle={{'--table-min-width': `${tableMinWidth}px`}}
                    searchPlaceholder={
                        searchPlaceholder || intl.formatMessage({id: 'sharedSearch'})
                    }
                    striped
                    hover
                    pagination
                >
                    {renderTableHeaderColumn}
                </BootstrapTable>
            </ThemedTableWrapper>
        );
    };

    return (
        <>
            {renderAlertConfirmation()}
            {renderTable()}
        </>
    );
};

export default DataTable;
